import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { viewReportTokenName } from '@app/config';
import { INTERVAL_TYPE } from '@main/enums';
import { ViewReportService } from '@main/services/view-report.service';
import { ADC_ERROR_CODE_ENUM } from '@shared/enums';
import { AdcError } from '@shared/models/error.model';

export const viewReportTokenInterceptor: HttpInterceptorFn = (req, next) => {
    const viewReportService = inject(ViewReportService);

    const getRequestWithToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
        request.clone({
            setHeaders: {
                [viewReportTokenName]: token,
            },
        });

    const isTokenRequest = (request: HttpRequest<unknown>): boolean =>
        !!request.url && request.url.indexOf('security/tokens/report') !== -1;

    const handle429 = (error: HttpErrorResponse): void => {
        let parsedError: { attributes?: { interval?: INTERVAL_TYPE } };

        try {
            parsedError = JSON.parse(error.error);
        } catch {}

        if (parsedError?.attributes) {
            const interval = parsedError.attributes.interval;
            viewReportService.showViewLimitMessage(interval);
        }
    };

    const handle400Error = (error: HttpErrorResponse, request: HttpRequest<unknown>): ReturnType<HttpInterceptorFn> => {
        const errorBody = request.responseType === 'json' ? error.error : JSON.parse(error.error);

        if (!isTokenRequest(request)) {
            return throwError(() => error);
        }

        if (/GROUP should have at most [0-9]+ entities\./.test(errorBody.message)) {
            return throwError(() => error);
        }

        return viewReportService
            .refreshToken()
            .pipe(switchMap((newToken: string) => next(getRequestWithToken(request, newToken))));
    };

    const token = viewReportService.viewToken;

    if (req.headers.get(viewReportTokenName) === '') {
        if (token) {
            req = getRequestWithToken(req, token);
        } else {
            throw new AdcError(ADC_ERROR_CODE_ENUM.GENERIC_ERROR, 'Report view token is not defined');
        }
    }

    return next(req).pipe(
        catchError((error: Error) => {
            if (error instanceof HttpErrorResponse) {
                switch (error.status) {
                    case 400:
                        return handle400Error(error, req);
                    case 429:
                        handle429(error);
                        return throwError(() => error);
                    default:
                        return throwError(() => error);
                }
            }
            return throwError(() => error);
        }),
    );
};
